import { NavLink, Outlet } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Wordmark } from '@/domain/wordmark';
import { useProfile } from '@/app/session';
import { RealtimeProvider } from '@/lib/realtime';
import { ErrorBoundary } from '@/app/error-boundary';
import { SignOutDialog } from './sign-out';

const NAV = [
  { label: 'Home', to: '/v' },
  { label: 'Submissions', to: '/v/submissions' },
  { label: 'Engagements', to: '/v/engagements' },
  { label: 'Remediation', to: '/v/remediation' },
  { label: 'Profile', to: '/v/profile' }
];

/**
 * Vendor portal chrome: a single dark topbar over the page surface.
 * The active item is a filled raised pill, not an underline - that is the operator console's
 * idiom, and the two shells stay visually distinct on purpose.
 */
export function VendorShell() {
  const { data: profile } = useProfile('vendor');

  const org = profile?.organization_name ?? profile?.name ?? '';
  const code = profile?.vendor_code ?? '';

  return (
    <RealtimeProvider realm="vendor" enabled={!!profile}>
      <div className="min-h-screen bg-background">
        <header className="flex h-14 items-center gap-8 border-b border-border bg-chrome px-6">
          <NavLink to="/v" className="shrink-0">
            <Wordmark height={13} />
          </NavLink>
          <nav className="flex items-center gap-1">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/v'}
                className={({ isActive }) =>
                  cn(
                    'rounded px-3 py-1.5 text-[13px] transition-colors',
                    isActive ? 'bg-raised text-foreground' : 'text-muted hover:text-foreground'
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <span className="font-mono text-[10px] tracking-[0.12em] text-muted uppercase">
              {[org, code].filter(Boolean).join(' · ')}
            </span>
            <SignOutDialog
              realm="vendor"
              profile={profile}
              trigger={
                <button
                  type="button"
                  title="Account"
                  aria-label="Account"
                  className="size-6 cursor-pointer rounded-full bg-primary transition-opacity hover:opacity-80"
                />
              }
            />
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1120px] px-6 py-12">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </RealtimeProvider>
  );
}
